/*
 * HarperKellee shared S3 upload helper.
 *
 * Encapsulates the AWS SDK load, Cognito credentials, and a single-file upload,
 * reusing MD's customer-uploads bucket/pool (the same target the design-help
 * dialog uploads to). Exposes:
 *
 *   window.HKS3.upload(file, onProgress) -> Promise<{ url, name }>
 *
 * Kept separate from hk-design-help.js so per-field "File Picker" personalization
 * inputs can upload without coupling to the design-help modal. The bucket/region/
 * pool constants intentionally mirror hk-design-help.js; keep the two in sync.
 */
(function (window, document) {
  'use strict';

  // S3 target (reuses MD's bucket/pool).
  var S3_BUCKET = 'md-customer-uploads';
  var S3_REGION = 'us-east-2';
  var S3_POOL_ID = 'us-east-2:32af96c7-3e43-4010-a185-e14c08cb63cf';
  var SESSION_PREFIX = 'hk-personalization';
  var MAX_BYTES = 20 * 1024 * 1024;

  // Lazy-load the AWS SDK once per page, then wire Cognito credentials.
  var awsLoading = null;
  function ensureAwsSDK() {
    if (window.AWS && window.AWS.config && window.AWS.config.credentials) { return Promise.resolve(); }
    if (awsLoading) { return awsLoading; }
    awsLoading = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = 'https://sdk.amazonaws.com/js/aws-sdk-2.1691.0.min.js';
      s.onload = function () {
        try {
          window.AWS.config.update({
            region: S3_REGION,
            credentials: new window.AWS.CognitoIdentityCredentials({ IdentityPoolId: S3_POOL_ID })
          });
          resolve();
        } catch (e) { reject(e); }
      };
      s.onerror = function () { reject(new Error('Failed to load AWS SDK')); };
      document.head.appendChild(s);
    });
    return awsLoading;
  }

  // Strip characters S3 keys dislike.
  function sanitizeName(name) { return name.replace(/[^a-zA-Z0-9._-]/g, '_'); }

  // One session prefix per page load so a customer's files group together.
  var sessionKey = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
  var fileCounter = 0;

  // Upload a single File to S3. onProgress(pct) is optional (0-100 integer).
  // Resolves with { url, name }; rejects on oversize or upload failure.
  function upload(file, onProgress) {
    if (file && file.size > MAX_BYTES) {
      return Promise.reject(new Error(file.name + ' is over the 20 MB limit.'));
    }
    return ensureAwsSDK().then(function () {
      var id = ++fileCounter;
      var key = SESSION_PREFIX + '/' + sessionKey + '/' + id + '_' + sanitizeName(file.name);
      var s3 = new window.AWS.S3({ apiVersion: '2006-03-01' });
      var managed = s3.upload({
        Bucket: S3_BUCKET,
        Key: key,
        Body: file,
        ContentType: file.type || 'application/octet-stream'
      });
      // Forward progress to the caller when asked.
      if (typeof onProgress === 'function') {
        managed.on('httpUploadProgress', function (evt) {
          if (!evt.total) { return; }
          onProgress(Math.round((evt.loaded / evt.total) * 100));
        });
      }
      return managed.promise().then(function (data) {
        return { url: data.Location, name: file.name };
      });
    });
  }

  window.HKS3 = { upload: upload, MAX_BYTES: MAX_BYTES };
})(window, document);
